import {css} from '@emotion/css';
import {TextField, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import {ChangeEvent, ComponentProps, forwardRef, ReactElement, ReactNode, Ref, useMemo} from 'react';

import {useTreeSelection} from 'utils/hooks/useTreeSelection';
import {filterTree, GenericTreeNode} from 'utils/treeUtils';
import {FuseSearchKey} from 'utils/hooks';
import {DISABLED_FILTERS_MESSAGE} from 'filters/features/FilterDialog/constants';

import LoadingIndicator from 'components/LoadingIndicator';
import ResponsiveBox from 'components/ResponsiveBox';
import {SearchProps} from 'components/Search';
import {Tree} from 'components/Tree/Tree';
import {TreeBasicNode, TreeKey} from 'components/Tree/TreeTypes';
import {useTreeSearchProps} from 'components/Tree/utils/useTreeSearch';
import TreePickerDetails from 'components/TreePicker/TreePickerDetails';
import CrossIcon from 'components/icons/cross.svg';
import SearchIcon from 'components/icons/search.svg';

import {BaseNoResultsPlaceholder} from 'modules/decisionsNext/components/placeholders/BaseNoResultsPlaceholder';

const SRoot = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(4),
  gap: theme.spacing(6),
  flex: 1,
}));

const SContentWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const STreeWrapper = styled('div')(() => ({
  flex: '1 1 auto',
  overflow: 'hidden',
  display: 'flex',
}));

const SResponsiveBox = styled(ResponsiveBox)(() => ({
  height: 'unset',
  flex: '1 1 auto',
}));

const sTree = css`
  padding: 0;
`;

const SCrossIcon = styled(CrossIcon)`
  cursor: pointer;
`;

const SDetailsAndContentWrapper = styled('div')(({theme}) => ({
  overflow: 'hidden',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),

  '& > :last-child': {
    flexGrow: 1,
  },
}));

export type FilterTreePickerProps<K extends TreeKey, T extends TreeBasicNode<K>> = {
  treeData: GenericTreeNode<T>[];
  isLoading?: boolean;
  defaultExpandedKeys?: K[];
  disabledKeys?: Set<K> | K[];
  isDisabled?: boolean;
  showSelectionCount?: boolean;
  searchKeys?: FuseSearchKey<T[]>[];
  entity: string;
  selected: K[];
  setSelected: (newSelected: K[]) => void;
  onSearchChange?: SearchProps['onChange'];
  selectionCount?: ReactNode;
  additionalFilters?: ReactNode;
  className?: string;
  withSearch?: boolean;
  treeHeight?: number;
};

export const FilterTreePicker = forwardRef(
  <K extends TreeKey, T extends TreeBasicNode<K>>(
    {
      treeData,
      isLoading,
      defaultExpandedKeys,
      disabledKeys,
      showSelectionCount,
      isDisabled,
      searchKeys,
      entity,
      selected,
      setSelected,
      onSearchChange,
      selectionCount: selectionCountPassed,
      additionalFilters,
      className,
      withSearch = true,
      treeHeight: customHeight,
    }: FilterTreePickerProps<K, T>,
    ref: Ref<HTMLDivElement>
  ) => {
    const {textFieldProps, treeProps, searchProps, isNothingFound, filteredTree} = useTreeSearchProps<K, T>({
      treeData,
      searchKeys,
    });

    const {onInverseSelection, onSelectAll, onSelectNone, selectionCount, onCheck} = useTreeSelection({
      treeData,
      filteredTreeData: useMemo(() => {
        const tree = searchProps.value ? filteredTree : treeData;

        if (!disabledKeys) {
          return tree;
        }

        const disabledSet = Array.isArray(disabledKeys) ? new Set(disabledKeys) : disabledKeys;

        return filterTree(tree, item => !disabledSet.has(item.key));
      }, [filteredTree, searchProps.value, disabledKeys, treeData]),
      selected,
      setSelected,
    });

    const normalizedTreeProps = {
      ...treeProps,
      treeData: searchProps.value ? filteredTree : treeData,
      virtual: true,
      disabled: isDisabled,
      disabledKeys,
      defaultExpandedKeys,
      checkedKeys: selected,
      onCheck,
      rootClassName: sTree,
    } satisfies ComponentProps<typeof Tree<K, T>>;

    return (
      <SRoot className={className} ref={ref}>
        {withSearch && (
          <TextField
            value={textFieldProps.value}
            onChange={event => {
              textFieldProps.onChange(event);
              onSearchChange?.(event as unknown as ChangeEvent<HTMLInputElement>, textFieldProps.value, 'change');
            }}
            placeholder={`Search ${entity}`}
            color='secondary'
            size='small'
            slotProps={{
              input: {
                startAdornment: <SearchIcon />,
                endAdornment: textFieldProps.value && (
                  <SCrossIcon
                    onClick={() => {
                      searchProps.onChange(undefined, '');
                      onSearchChange?.({target: {value: ''}} as unknown as ChangeEvent<HTMLInputElement>, '', 'clear');
                    }}
                  />
                ),
              },
            }}
            fullWidth
          />
        )}

        {additionalFilters}

        <SDetailsAndContentWrapper>
          {isDisabled && !showSelectionCount ? (
            <Typography variant='subtitle3' color='text.secondary' paragraph marginBottom={0} lineHeight='32px'>
              {DISABLED_FILTERS_MESSAGE}
            </Typography>
          ) : (
            <TreePickerDetails
              selectionCount={selectionCountPassed ?? selectionCount}
              onInverseSelection={onInverseSelection}
              onSelectAll={onSelectAll}
              onSelectNone={onSelectNone}
              isDisabled={isDisabled}
            />
          )}

          <SContentWrapper>
            {isLoading ? (
              <LoadingIndicator />
            ) : isNothingFound ? (
              <BaseNoResultsPlaceholder
                size='small'
                minHeight={customHeight ?? 264}
                padding={customHeight ? 0 : undefined}
                heading='There is no data to show.'
                subheading='Try another keyword to find data.'
                subheadingColor='text.secondary'
              />
            ) : customHeight ? (
              <Tree<K, T> {...normalizedTreeProps} height={customHeight} />
            ) : (
              <STreeWrapper>
                <SResponsiveBox>
                  {({height}) => <Tree<K, T> {...normalizedTreeProps} height={Math.max(height, 320)} />}
                </SResponsiveBox>
              </STreeWrapper>
            )}
          </SContentWrapper>
        </SDetailsAndContentWrapper>
      </SRoot>
    );
  }
) as unknown as <K extends TreeKey = string, T extends TreeBasicNode<K> = TreeBasicNode<K>>(
  props: FilterTreePickerProps<K, T> & {ref?: Ref<HTMLDivElement>}
) => ReactElement;
